import { useApi, useMessage } from 'app/context';
import { buildErrorMessage } from 'app/utils';
import { IMonthlyStatisticsSchema, IStatisticsOverviewSchema } from 'generated/axios';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from './useForm';

export const useStatistics = (medicalCenterId: string) => {
  const { statisticsApi } = useApi();
  const { error } = useMessage();

  const [statistics, setStatistics] = useState<IStatisticsOverviewSchema>();
  const [monthlyStatistics, setMonthlyStatistics] = useState<IMonthlyStatisticsSchema>();
  const [request, setRequest] = useState<MonthlyOverviewRequest>();
  const [loading, setLoading] = useState<boolean>(false);

  const get = useCallback(() => {
    setLoading(true);
    statisticsApi
      .webGetStatistics(medicalCenterId)
      .then((resp) => {
        if (resp.status >= 400) {
          return Promise.reject(resp);
        }
        setStatistics(resp.data);
      })
      .catch((err) => {
        error({ message: buildErrorMessage(err) });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statisticsApi, medicalCenterId]);

  useEffect(() => {
    get();
  }, [get]);

  const getMonthlyOverview = useCallback(
    (month?: number | undefined, year?: number | undefined) => {
      setLoading(true);
      statisticsApi
        .webGetMonthlyStatistics(medicalCenterId, month, year)
        .then((resp) => {
          if (resp.status >= 400) {
            return Promise.reject(resp);
          }
          setMonthlyStatistics(resp.data);
        })
        .catch((err) => {
          error({ message: buildErrorMessage(err) });
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statisticsApi, medicalCenterId],
  );

  useEffect(() => {
    setRequest(() => ({ month: monthlyStatistics?.month, year: monthlyStatistics?.year }));
  }, [monthlyStatistics]);

  type MonthlyOverviewRequest = {
    month: number | undefined;
    year: number | undefined;
  };

  const monthlyOverviewForm = useForm<MonthlyOverviewRequest>({
    defaultValues: request,
    onSubmit: ({ month, year }) => getMonthlyOverview(month, year),
  });

  return { statistics, loading, monthlyOverviewForm, monthlyStatistics };
};
